import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";

import { AgentRecord } from "../AgentRecord";

export interface AgentDirectory {
  /** Lookup by ``agent_id``. Empty until the underlying fetch settles. */
  byId: Map<string, AgentRecord>;
  /** Agents in the order the server returned them — useful for option lists. */
  agents: AgentRecord[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Fetches every Agent in the caller's org and returns lookups by id plus
 * the raw list. Backs both the Agent filter dropdown on the AgentThreads
 * table (needs the list as label/value options) and the per-row Agent
 * cell renderer (needs id → name lookup).
 *
 * Pagination: the backend route ``GET /v1/ai/agents`` hardcodes
 * ``limit=100`` and does not accept a ``limit`` query parameter, so this
 * hook chains ``next_token`` calls until the cursor is exhausted. The
 * whole-org result is cached under a single TanStack query key, so the
 * hook mounted from multiple places on the same page shares one
 * in-flight fetch and one cached snapshot.
 */
export function useAgentDirectory(orgId: string | undefined): AgentDirectory {
  const { agentService } = useDomainServices();

  const query = useQuery<AgentRecord[], Error>({
    queryKey: ["agentDirectory", orgId],
    queryFn: async ({ signal }) => {
      if (orgId === undefined) {
        return [];
      }
      const collected: AgentRecord[] = [];
      let pageToken: string | undefined = undefined;
      do {
        const page = await agentService.listAgents({
          orgId,
          pageToken,
          abortSignal: signal,
        });
        collected.push(...page.items);
        // ``next_token`` is ``string | null`` on the wire; coerce ``null``
        // (and any other falsy) to ``undefined`` so the loop predicate
        // stays a single ``!== undefined`` check.
        pageToken = page.next_token || undefined;
      } while (pageToken !== undefined);
      return collected;
    },
    enabled: orgId !== undefined,
    staleTime: 30_000,
  });

  const agents = query.data ?? EMPTY_AGENTS;

  // Memoize on ``agents`` identity. TanStack Query produces a new array
  // reference only when the underlying data actually changes (it returns
  // the same reference for background refetches that resolve to the same
  // payload thanks to ``structuralSharing``), so cell-render consumers
  // that depend on ``byId`` won't churn on every revalidation.
  const byId = React.useMemo<Map<string, AgentRecord>>(() => {
    const map = new Map<string, AgentRecord>();
    for (const agent of agents) {
      map.set(agent.agent_id, agent);
    }
    return map;
  }, [agents]);

  return {
    byId,
    agents,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

// Module-level constant so the ``agents`` fallback keeps a stable
// reference across renders while the query is pending — avoids
// recomputing ``byId`` on every render that happens before data arrives.
const EMPTY_AGENTS: AgentRecord[] = [];
